import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import { over, get, set } from '@totalsoft/change-tracking-react';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow, Grid, Checkbox } from '@mui/material';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { Close, Check } from '@mui/icons-material';

const FinancialProductDetailsInsurersTableRow = ({
  canWrite,
  dataLens,
  index,
  onCancelEdit,
  onDelete,
  onSave,
  insurersList,
  insuranceTypeList,
  conventionList,
  validation
}) => {
  const [t] = useTranslation();
  const data = dataLens |> get;
  const filteredConventionList = useMemo(() => 
  {
    if(data?.insurerId && data?.insTypeId)
      return conventionList.filter(item => item.insurerId === data.insurerId && item.insTypeId === data.insTypeId);
    if(data?.insTypeId)
      return conventionList.filter(item => item.insTypeId === data.insTypeId);
    if(data?.insurerId)
      return conventionList.filter(item => item.insurerId === data.insurerId);
     return [];
  }, [conventionList, data]);

  return (
    <TableRow>
      {data.isInEditing ? (
        <>
         <TableCell>
            <Autocomplete
              valueKey={'id'}
              labelKey={'name'}
              value={{ id: data.insTypeId, name: data.insuranceTypeName }}
              options={insuranceTypeList}
              onChange={event => {
                over(dataLens, oldState => ({
                              ...oldState, 
                              insuranceConventionId: null, 
                              insuranceConventionName: null,
                              insTypeId: event.id,
                              insuranceTypeName: event.name
                            }));
              }}
              error={!isValid(validation.insTypeId)}
              helperText={getErrors(validation.insTypeId)}
              required
            />
          </TableCell>
          <TableCell>
            <Autocomplete
              valueKey={'id'}
              labelKey={'name'}
              value={{ id: data.insurerId, name: data.insurerName }}
              options={insurersList}
              onChange={event => {
                over(dataLens, oldState => ({
                  ...oldState, 
                  insuranceConventionId: null, 
                  insuranceConventionName: null,
                  insurerId: event.id,
                  insurerName: event.name
                }));
              }}
              error={!isValid(validation.insurerId)}
              helperText={getErrors(validation.insurerId)}
              required
            />
          </TableCell>
          <TableCell>
            <Autocomplete
              valueKey={'productId'}
              labelKey={'productName'}
              value={{ productId: data.insuranceConventionId, productName: data.insuranceConventionName }}
              options={filteredConventionList}
              onChange={event => {
                over(dataLens, oldState => ({
                  ...oldState, 
                  insuranceConventionId: event.productId, 
                  insuranceConventionName: event.productName
                }));
              }}
              error={!isValid(validation.insuranceConventionId)}
              helperText={getErrors(validation.insuranceConventionId)}
              required
            />
          </TableCell>          
          <TableCell>
              <Checkbox
                disabled={!canWrite}
                checked={data.implicit || false}
                onChange={() => set(dataLens.implicit)(!data.implicit)}
              />
          </TableCell>
          <TableCell>
              <Checkbox
                disabled={!canWrite}
                checked={data.mandatory || false}
                onChange={() => set(dataLens.mandatory)(!data.mandatory)}
              />
          </TableCell>
          <TableCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton
                type='save'
                fontSize='medium' 
                color='transparent'
                tooltip={t('FinancialProduct.Save')}
                disabled={data.isSaving || !canWrite}
                onClick={() => onSave(index)}
              />
              <IconButton
                type='cancel'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.CloseEdit')}
                onClick={() => onCancelEdit(data.leasingFinancialProductXLeasInsurerId, index)}
              />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(data.leasingFinancialProductXLeasInsurerId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell>{data.insuranceTypeName}</TableCell>
          <TableCell>{data.insurerName}</TableCell>
          <TableCell>{data.insuranceConventionName}</TableCell>
          <TableCell>
          <>{(data.implicit) === true ? <Check color='success' /> : <Close />}</>  
          </TableCell>
          <TableCell>
          <>{(data.mandatory) === true ? <Check color='success' /> : <Close />}</> 
          </TableCell>
          <TableCell align='right'>
            <Grid container wrap='nowrap' justifyContent='flex-end'>
              <IconButton 
                type='edit' 
                fontSize='medium' 
                color='transparent' 
                tooltip={t('FinancialProduct.Edit')} 
                onClick={() => set(dataLens.isInEditing)(true)}
                disabled={!canWrite} />
              <IconButton
                type='delete'
                fontSize='medium'
                color='transparent'
                tooltip={t('FinancialProduct.Delete')}
                onClick={() => onDelete(data.leasingFinancialProductXLeasInsurerId, index)}
                disabled={!canWrite}
              />
            </Grid>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

FinancialProductDetailsInsurersTableRow.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  dataLens: PropTypes.object,
  index: PropTypes.number,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  insurersList: PropTypes.array.isRequired,
  insuranceTypeList: PropTypes.array.isRequired,
  conventionList: PropTypes.array.isRequired,
  validation: PropTypes.object.isRequired
};

export default FinancialProductDetailsInsurersTableRow;
